/**
 * Mark Important Dialog — set/clear Important Finding flags on a study.
 *
 * Fields mirror GWT ImportantFindingWindow:
 * - isImportant toggle
 * - reason, note, phone
 * - actionNeeded, viewed
 * - ackTime (acknowledge within N hours)
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography,
  FormControlLabel, Checkbox, MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface Props {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

const ACK_HOURS = [0, 1, 2, 4, 8, 12, 24, 48, 72];

export const MarkImportantDialog = ({ open, study, onClose, onComplete }: Props) => {
  const [saving, setSaving] = useState(false);
  const [isImportant, setIsImportant] = useState(true);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [phone, setPhone] = useState('');
  const [actionNeeded, setActionNeeded] = useState(false);
  const [ackTime, setAckTime] = useState<number>(0);

  const imp = study?.important;

  useEffect(() => {
    if (open) {
      setIsImportant(imp?.isImportant ?? true);
      setReason(imp?.reason ?? '');
      setNote(imp?.note ?? '');
      setPhone(imp?.phone ?? '');
      setActionNeeded(imp?.actionNeeded ?? false);
      setAckTime(imp?.ackTime ?? 0);
    }
  }, [open, imp]);

  const handleSubmit = async () => {
    if (!study) return;
    setSaving(true);
    try {
      await api.markImportant({
        objKey: study.objKey,
        isImportant,
        reason: reason || undefined,
        actionNeeded,
        note: note || undefined,
        phone: phone || undefined,
        ackTime: ackTime || undefined,
      });
      pushSuccess(isImportant ? 'Marked as Important' : 'Important flag cleared');
      onComplete();
      onClose();
    } catch (e) {
      pushError('Failed to mark important: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>Important Finding</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {study && (
          <Typography variant="body2" color="text.secondary">
            {study.niceName ?? study.name} — {study.acc}
          </Typography>
        )}

        <FormControlLabel
          control={<Checkbox checked={isImportant} onChange={(_, v) => setIsImportant(v)} />}
          label="Mark as Important Finding"
        />

        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          multiline
          rows={2}
          fullWidth
        />

        <TextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
          multiline
          rows={2}
          fullWidth
        />

        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          size="small"
          fullWidth
        />

        <FormControlLabel
          control={<Checkbox checked={actionNeeded} onChange={(_, v) => setActionNeeded(v)} />}
          label="Action Needed"
        />

        <FormControl size="small" fullWidth>
          <InputLabel>Acknowledge Within (hours)</InputLabel>
          <Select
            value={ackTime}
            onChange={(e) => setAckTime(Number(e.target.value))}
            label="Acknowledge Within (hours)"
          >
            <MenuItem value={0}>No deadline</MenuItem>
            {ACK_HOURS.filter((h) => h > 0).map((h) => (
              <MenuItem key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
